/** Subscription billing (ADR-0033): owner billing page, status banner, platform invoices. */
export const enBillingSaas = {
  // ---- Errors raised by plan/lifecycle enforcement
  'error.subscriptionReadOnly':
    'The account is read-only because a subscription invoice is unpaid. You can still view data and settle open bills.',
  'error.moduleNotInPlan': 'This feature is not included in your subscription plan.',
  'error.planLimitReached':
    'Your subscription plan limit has been reached. Upgrade the plan to add more.',
  'error.tenantSuspended':
    'This account is suspended. Contact the business owner or our support team.',

  // ---- Back-office navigation
  'subs.nav': 'Subscription & invoices',

  // ---- Owner billing page
  'subs.title': 'Subscription & invoices',
  'subs.subtitle': 'Your plan, subscription status, usage and invoices from {product}.',
  'subs.noPermission': 'Your role cannot view subscription invoices.',
  'subs.status.trialing': 'Trial',
  'subs.status.active': 'Active',
  'subs.status.past_due': 'Payment overdue',
  'subs.status.read_only': 'Read-only',
  'subs.status.suspended': 'Suspended',
  'subs.status.cancelled': 'Cancelled',
  'subs.complimentary': 'Free licence',
  'subs.complimentaryDesc': 'Your account has a free licence: no invoices and nothing to pay.',
  'subs.planTitle': 'Plan & status',
  'subs.planDesc': 'Prices exclude VAT (PPN). Invoices are issued before each period starts.',
  'subs.pricePerMonth': '{price} / month',
  'subs.pricePerYear': '{price} / year',
  'subs.trialEnds': 'Trial ends',
  'subs.periodEnds': 'Paid until',
  'subs.balanceDue': 'Amount due',
  'subs.readOnlyAt': 'Read-only from',
  'subs.suspendedAt': 'Suspended from',
  'subs.noPeriod': 'No paid period yet',
  'subs.usageTitle': 'Usage',
  'subs.usageDesc': 'What you use now against the limits of your plan.',
  'subs.usage.branches': 'Active branches',
  'subs.usage.staff': 'Staff accounts',
  'subs.usage.devices': 'Active devices',
  'subs.usage.of': '{used} of {max}',
  'subs.usage.unlimited': '{used} · unlimited',
  'subs.modulesTitle': 'Included features',
  'subs.module.pos': 'Cashier (POS)',
  'subs.module.kds': 'Kitchen display',
  'subs.module.service': 'Table service',
  'subs.module.guest_qr': 'QR menu',
  'subs.module.guest_ordering': 'Guest QR ordering',
  'subs.module.inventory': 'Inventory',
  'subs.module.procurement': 'Purchasing',
  'subs.module.ap': 'Supplier invoices',
  'subs.module.approvals': 'Approvals',
  'subs.module.budgets': 'Budgets',
  'subs.module.custom_domain': 'Custom domain',
  'subs.module.warehouse': 'Warehouse',
  'subs.payTitle': 'How to pay',
  'subs.payDesc':
    'Pay by bank transfer and put the invoice number in the transfer note. Our team records the payment and your account is updated automatically.',
  'subs.bank': 'Bank',
  'subs.accountNumber': 'Account number',
  'subs.accountName': 'Account holder',
  'subs.transferNote': 'Transfer note',
  'subs.bankMissing': 'Bank details have not been set yet. Contact our team for payment details.',
  'subs.invoicesTitle': 'Invoices',
  'subs.invoicesDesc': 'Subscription invoices issued to {tenant}, newest first.',
  'subs.invoicesEmpty':
    'No invoices yet — the first invoice is issued a week before the trial or period ends.',
  'subs.col.number': 'Invoice',
  'subs.col.period': 'Period',
  'subs.col.total': 'Total',
  'subs.col.due': 'Due',
  'subs.col.status': 'Status',
  'subs.view': 'View',
  'subs.invoice.open': 'Unpaid',
  'subs.invoice.overdue': 'Overdue',
  'subs.invoice.paid': 'Paid',
  'subs.invoice.void': 'Void',
  'subs.period': '{start} – {end}',

  // ---- Printable invoice
  'subs.detail.back': 'Back to invoices',
  'subs.detail.title': 'Invoice {number}',
  'subs.detail.heading': 'Subscription invoice',
  'subs.detail.issuer': 'From',
  'subs.detail.customer': 'Billed to',
  'subs.detail.taxId': 'Tax ID (NPWP): {id}',
  'subs.detail.issued': 'Issued',
  'subs.detail.due': 'Due date',
  'subs.detail.paidOn': 'Paid on {date}',
  'subs.detail.description': 'Description',
  'subs.detail.amount': 'Amount',
  'subs.detail.line': '{plan} plan subscription, {period}',
  'subs.detail.subtotal': 'Subtotal',
  'subs.detail.tax': 'VAT (PPN) {rate}',
  'subs.detail.total': 'Total',
  'subs.detail.notFound': 'This invoice was not found.',

  // ---- App-wide banner
  'subs.banner.trialing':
    'Your trial ends on {date} ({days} days left). Pay the invoice to keep using {product} without interruption.',
  'subs.banner.trialingToday':
    'Your trial ends today. Pay the invoice to keep using {product} without interruption.',
  'subs.banner.past_due':
    'The subscription payment is overdue. Pay the invoice before the account becomes read-only.',
  'subs.banner.read_only':
    'The account is read-only because the subscription invoice is unpaid. You can still view data and settle open bills.',
  'subs.banner.suspended':
    'The account is suspended because the subscription invoice is unpaid. Pay the invoice to reactivate it.',
  'subs.banner.action': 'View invoices',
  'subs.banner.askOwner': 'Ask the business owner to pay the invoice.',

  // ---- Platform console: subscription invoices
  'subs.platform.nav': 'Invoices',
  'subs.platform.title': 'Subscription invoices',
  'subs.platform.subtitle':
    'Invoices issued to tenants. Record a bank transfer only after the money is on the statement.',
  'subs.platform.filterStatus': 'Status',
  'subs.platform.filterTenant': 'Tenant',
  'subs.platform.all': 'All',
  'subs.platform.allTenants': 'All tenants',
  'subs.platform.empty':
    'No invoices match. Invoices appear once the billing cycle or an operator issues them.',
  'subs.platform.col.tenant': 'Tenant',
  'subs.platform.issue': 'Issue invoice',
  'subs.platform.issueTitle': 'Issue an invoice',
  'subs.platform.issueDesc':
    'For the next period of the tenant’s plan (or the plan you choose). Paying it moves the tenant onto that plan.',
  'subs.platform.plan': 'Plan',
  'subs.platform.planCurrent': 'Current plan',
  'subs.platform.issuing': 'Issuing…',
  'subs.platform.issued': 'Invoice {number} issued.',
  'subs.platform.runCycle': 'Run billing now',
  'subs.platform.running': 'Running…',
  'subs.platform.cycleDone':
    'Billing run: {issued} invoices issued, {reminders} reminders, {transitions} status changes.',
  'subs.platform.record': 'Record payment',
  'subs.platform.recordTitle': 'Record payment for {number}',
  'subs.platform.recordDesc':
    '{tenant} · total {total}. Only full payments by bank transfer; the tenant is reactivated automatically.',
  'subs.platform.amount': 'Amount received',
  'subs.platform.paidAt': 'Received on',
  'subs.platform.reference': 'Bank reference',
  'subs.platform.referenceHint': 'As shown on the bank statement.',
  'subs.platform.recording': 'Recording…',
  'subs.platform.recorded': 'Payment for {number} recorded.',
  'subs.platform.amountMismatch': 'The amount must equal the invoice total ({total}).',
  'subs.platform.void': 'Void',
  'subs.platform.voidTitle': 'Void {number}?',
  'subs.platform.voidDesc': 'A voided invoice cannot be paid or reopened.',
  'subs.platform.voidReason': 'Reason',
  'subs.platform.voiding': 'Voiding…',
  'subs.platform.voided': 'Invoice {number} voided.',
  'subs.platform.noAccess': 'Only platform operators can see subscription invoices.',
} as const;
